import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2, Printer, Shirt } from 'lucide-react';
import { fetchBureauBenevoles, fetchReferentielBenevole } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { imprimerListe } from '../utils/impression';

/**
 * Le détail des tailles, personne par personne.
 *
 * L'onglet Tailles de la page Membres compte les pièces à commander ; cette
 * page dit à qui elles vont — une ligne par bénévole, une colonne par
 * vêtement — pour distribuer les cartons quand la commande arrive.
 */
export default function BureauTaillesPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const perso = usePersonnalisation();
  const aAcces = aLeDroit('voir_benevoles');

  const benevolesQuery = useQuery({
    queryKey: ['bureau-benevoles'],
    queryFn: fetchBureauBenevoles,
    enabled: aAcces,
  });
  const referentielQuery = useQuery({
    queryKey: ['benevole-referentiel'],
    queryFn: fetchReferentielBenevole,
    enabled: aAcces,
  });

  if (authLoading || (aAcces && (benevolesQuery.isPending || referentielQuery.isPending))) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!aAcces) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Cette page dépend de votre fonction au bureau : un administrateur peut l'ouvrir
              dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const benevoles: { fullName?: string; tailles?: Record<string, string> }[] =
    benevolesQuery.data?.benevoles ?? [];
  const vetements: { code: string; label: string; tailles: string[] }[] =
    referentielQuery.data?.vetements ?? [];

  // Seules les fiches qui ont déclaré au moins une taille figurent : la page
  // sert à distribuer des vêtements, pas à lister les absents.
  const avecTailles = benevoles
    .filter((b) => b.tailles && Object.keys(b.tailles).length > 0)
    .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '', 'fr'));
  const sansTaille = benevoles.length - avecTailles.length;
  const totalPieces = avecTailles.reduce(
    (somme, b) => somme + Object.keys(b.tailles ?? {}).length,
    0
  );

  const imprimer = () => {
    imprimerListe({
      titre: 'Vêtements par personne',
      association: perso?.nomAssociation ?? undefined,
      sousTitre: `${avecTailles.length} bénévole(s), ${totalPieces} pièce(s) — tailles déclarées sur les fiches.`,
      totaux: [
        { libelle: 'pièce(s) au total', valeur: String(totalPieces) },
        ...(sansTaille > 0 ? [{ libelle: 'fiche(s) sans taille déclarée', valeur: String(sansTaille) }] : []),
      ],
      colonnes: [
        { titre: 'Bénévole', valeur: (l: any) => l.nom },
        ...vetements.map((v) => ({
          titre: v.label,
          valeur: (l: any) => l.tailles[v.code] ?? '',
          alignement: 'centre' as const,
        })),
      ],
      lignes: avecTailles.map((b) => ({ nom: b.fullName ?? '', tailles: b.tailles ?? {} })),
      colonnePointage: 'Remis',
    });
  };

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div>
        <Link
          to="/bureau/membres?vue=tailles"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux tailles
        </Link>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Shirt className="w-7 h-7 text-primary-700" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Vêtements par personne
            </h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
              {avecTailles.length} bénévole(s), {totalPieces} pièce(s)
              {sansTaille > 0 && <> — {sansTaille} fiche(s) sans taille déclarée</>}.
            </p>
          </div>
        </div>
        {avecTailles.length > 0 && (
          <button onClick={imprimer} className="btn-outline inline-flex items-center gap-2 text-sm px-4 py-2">
            <Printer className="w-4 h-4" /> Exporter en PDF
          </button>
        )}
      </div>

      {avecTailles.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Aucune taille déclarée pour le moment : chaque bénévole renseigne les siennes depuis
          « Mes informations ».
        </p>
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                <th className="px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Bénévole</th>
                {vetements.map((v) => (
                  <th key={v.code} className="px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300 text-center whitespace-nowrap">
                    {v.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {avecTailles.map((b) => (
                <tr key={b.fullName}>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {b.fullName}
                  </td>
                  {vetements.map((v) => (
                    <td key={v.code} className="px-3 py-2 text-center tabular-nums">
                      {b.tailles?.[v.code] ? (
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{b.tailles[v.code]}</span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
